class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // NS fix (2026-08-11): user-initiated opens re-sync the drawer body from the server first.
    // Carts changed outside the theme's own add path (direct /cart/add.js calls, other tabs)
    // otherwise open on the stale page-load render. Programmatic opens after renderContents
    // pass no triggeredBy and skip this, since they just painted fresh sections.
    if (triggeredBy) this.refresh();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const trapCartFocus = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // NS fix (K-6, 2026-08-12): reduced motion zeroes every transition
      // (ns-components.css @media reduced-motion: * { transition: none !important }), so
      // 'transitionend' never fires and focus never enters the dialog (WCAG 2.4.3). Mirror
      // Dawn's menu-drawer: trap focus at once under reduced motion, else on the transition.
      !reducedMotion || reducedMotion.matches
        ? trapCartFocus()
        : this.addEventListener('transitionend', trapCartFocus, { once: true });
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // NS fix (2026-08-11): the empty-state class lives on the <cart-drawer> ROOT in this theme's
    // markup. A stale root .is-empty after an add hid the freshly rendered rows
    // (.is-empty .cart__contents is display:none in component-cart.css) while the new footer
    // total stayed visible. renderContents only runs after a successful add, so never empty here.
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // NS (2026-08-11): re-render the drawer body from the live cart. Replaces the whole
  // #CartDrawer contents (same surface renderContents paints) and mirrors the server's
  // empty-state class onto the root, so empty-to-filled and filled-to-empty both settle right.
  refresh() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceDrawer = parsedHTML.querySelector('cart-drawer');
        const sourceContents = parsedHTML.querySelector('#CartDrawer');
        const targetContents = this.querySelector('#CartDrawer');
        if (!sourceDrawer || !sourceContents || !targetContents) return;
        this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        if (targetContents.innerHTML === sourceContents.innerHTML) return;
        targetContents.innerHTML = sourceContents.innerHTML;
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
